import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, InputAdornment } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_BASE_URL } from './config';

const ForgotPasswordPage = ({ initialEmail = '' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [email, setEmail] = useState(initialEmail);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [emailError, setEmailError] = useState('');

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        if (value && !validateEmail(value)) {
            setEmailError(t('invalid_email_format'));
        } else {
            setEmailError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !validateEmail(email)) {
            setError(t('enter_valid_email'));
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/request_password_reset.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || t('reset_request_failed'));
            }
        } catch (err) {
            setError(t('network_error'));
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    height: '100vh',
                    boxSizing: 'border-box',
                }}
            >
                <Paper
                    elevation={6}
                    sx={{
                        padding: { xs: 4, md: 6 },
                        borderRadius: 2,
                        textAlign: 'center',
                        maxWidth: 450,
                        width: '100%',
                    }}
                >
                    <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        bgcolor: '#e3f2fd', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                    </Box>
                    <Typography variant="h5" component="h2" gutterBottom>
                        {t('check_your_email')}
                    </Typography>
                    <Typography variant="body1" paragraph color="text.secondary">
                        {t('reset_email_sent_message')}
                    </Typography>
                    <Typography variant="body2" paragraph color="text.secondary">
                        <strong>{email}</strong>
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                        {t('check_spam_folder')}
                    </Alert>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/')}
                        sx={{ mt: 2 }}
                    >
                        {t('back_to_login')}
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 4,
                height: '100vh',
                boxSizing: 'border-box',
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    padding: { xs: 4, md: 6 },
                    borderRadius: 2,
                    textAlign: 'center',
                    maxWidth: 450,
                    width: '100%',
                }}
            >
                <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                </Box>
                <Typography variant="h5" component="h2" gutterBottom>
                    {t('forgot_password_title')}
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                    {t('forgot_password_prompt')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label={t('email_label')}
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        margin="normal"
                        required
                        disabled={isLoading}
                        error={!!emailError}
                        helperText={emailError}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isLoading || !!emailError}
                        sx={{
                            mt: 3,
                            mb: 2,
                            padding: '12px 30px',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : t('send_reset_link')}
                    </Button>
                    
                    <Button
                        variant="text"
                        onClick={() => navigate('/')}
                        sx={{ mt: 1 }}
                    >
                        {t('back_to_login')}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default ForgotPasswordPage;
